(() => {
  const root = document.querySelector('[data-global-assistant]');
  const launcher = document.querySelector('.global-assistant-launcher');
  if (!root || !launcher) return;
  const openers = [...document.querySelectorAll('[data-global-assistant-open]')];

  const panel = root.querySelector('.global-assistant__panel');
  const thread = root.querySelector('[data-global-assistant-thread]');
  const form = root.querySelector('[data-global-assistant-form]');
  const input = root.querySelector('[data-global-assistant-input]');
  const sendButton = root.querySelector('[data-global-assistant-send]');
  const stopButton = root.querySelector('[data-global-assistant-stop]');
  const status = root.querySelector('[data-global-assistant-status]');
  const quickPromptButtons = [...root.querySelectorAll('[data-assistant-prompt]')];
  const contextNode = document.querySelector('[data-global-assistant-context]');
  const catalogNode = document.querySelector('[data-global-assistant-catalog]');
  const storageKey = 'floralsculpt_assistant_v7';
  let context = {};
  let catalog = [];
  let messages = [];
  let recommendation = {};
  let bridgesMuted = false;
  let busy = false;
  let timer = 0;
  let requestController = null;
  let lastOpener = launcher;
  let messageSequence = Date.now();

  try { context = JSON.parse(contextNode?.textContent || '{}'); } catch (_) {}
  try { catalog = JSON.parse(catalogNode?.textContent || '[]').filter(Boolean); } catch (_) {}
  const catalogByHandle = new Map(catalog.filter(record => record?.handle).map(record => [String(record.handle),record]));

  const escapeHTML = value => String(value ?? '').replace(/[&<>'"]/g, character => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[character]));
  // Validate at render time too: older session messages can contain obsolete model-generated URLs.
  const safeActions = actions => (Array.isArray(actions) ? actions : []).flatMap(action => {
    if (!action || typeof action !== 'object') return [];
    if (action.href) {
      const knownProducts = [...catalog,...(context.product ? [context.product] : []),...messages.flatMap(message => message.products || [])];
      const route = window.FloralSculptSupportRoutes?.resolve(action.href, knownProducts);
      return route ? [{...action,href:route.href,label:route.label}] : [];
    }
    return action.value && action.label ? [action] : [];
  });
  const ensureMessageId = message => {
    if (message && !message.id) message.id = `assistant-${messageSequence++}`;
    return message;
  };
  const safeSession = {
    read() {
      try { return JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch (_) { return null; }
    },
    write() {
      try { sessionStorage.setItem(storageKey, JSON.stringify({messages:messages.slice(-16), recommendation, bridgesMuted})); } catch (_) {}
    },
    clear() {
      try { sessionStorage.removeItem(storageKey); } catch (_) {}
    }
  };

  const restored = safeSession.read();
  if (restored) {
    messages = Array.isArray(restored.messages) ? restored.messages : [];
    recommendation = restored.recommendation || {};
    bridgesMuted = Boolean(restored.bridgesMuted);
  }

  const welcomeCopy = () => {
    if (context.product) return `Ask me how ${context.product.title || 'this piece'} could work in your room, what to pair it with, or start an editable arrangement.`;
    if (context.page_type === 'collection') return `I can narrow down ${context.collection?.title || 'this collection'}, compare pieces, or help you build a complete arrangement.`;
    if (context.template === 'index') return 'Tell me about a room, a feeling or a piece you already own. I can answer a quick question or shape an editable arrangement.';
    return 'Ask a quick product question, get styling guidance, or let me shape an editable arrangement for your space.';
  };

  const renderWelcome = () => {
    thread.innerHTML = `<div class="global-assistant__welcome">
      <div class="global-assistant__intro"><h2>What can I help you with?</h2><p>${escapeHTML(welcomeCopy())}</p></div>
      <div class="global-assistant__starters">
        <span>Try asking</span>
        <button type="button" data-assistant-prompt="What pairs well with roses?">What pairs well with roses?</button>
        <button type="button" data-assistant-prompt="Help me style a console.">Help me style a console</button>
        <button type="button" data-assistant-prompt="Build an arrangement for me.">Build an arrangement for me</button>
      </div>
    </div>`;
  };

  const isBlankStudioRoute = href => {
    if (!href) return false;
    const url = new URL(href, location.origin);
    return url.pathname === '/collections/all' && url.searchParams.get('view') === 'design-studio'
      && !['set','look','product','product_id','ai_plan'].some(key => url.searchParams.has(key));
  };

  const renderMessages = () => {
    if (!messages.length) { renderWelcome(); return; }
    thread.innerHTML = `<div class="global-assistant__messages">${messages.map(message => {
      if (message.type === 'thinking') return '<div class="global-assistant__message global-assistant__message--assistant global-assistant__message--thinking"><span class="global-assistant__thinking-native" aria-label="Considering your request"><span class="global-assistant__thinking-bars" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i><i></i><i></i></span><small>Shaping a response</small></span></div>';
      if (message.type === 'plans') return renderPlanMessage(message);
      if (message.type === 'products') return renderProductMessage(message);
      const planMessage = messages.find(item => item.type === 'plans' && item.id === message.studioPlanMessageId);
      const planIndex = planMessage?.plans?.findIndex(plan => plan?.lines?.some(line => line?.record?.handle)) ?? -1;
      const productMessage = messages.find(item => item.type === 'products' && item.id === message.studioProductMessageId);
      const actions = Array.isArray(message.actions) && message.actions.length ? `<div class="global-assistant__choices">${safeActions(message.actions).map(action => {
        if (isBlankStudioRoute(action.href) && planIndex >= 0) return `<button type="button" class="global-assistant__action${action.quiet ? ' global-assistant__action--quiet' : ''}" data-assistant-plan-edit="${planIndex}" data-assistant-message-id="${escapeHTML(planMessage.id)}">${escapeHTML(action.label)}</button>`;
        if (isBlankStudioRoute(action.href) && productMessage?.products?.some(product => product?.kind !== 'set' && !isFinishedSetProduct(product) && product?.handle && product.category !== 'vase')) return `<button type="button" class="global-assistant__action${action.quiet ? ' global-assistant__action--quiet' : ''}" data-assistant-products-edit="${escapeHTML(productMessage.id)}">${escapeHTML(action.label)}</button>`;
        return action.href
          ? `<a class="global-assistant__action${action.quiet ? ' global-assistant__action--quiet' : ''}" href="${escapeHTML(action.href)}">${escapeHTML(action.label)}</a>`
          : `<button type="button" class="global-assistant__choice" data-assistant-choice="${escapeHTML(action.value)}">${escapeHTML(action.label)}</button>`;
      }).join('')}</div>` : '';
      const summary = message.summary ? `<div class="global-assistant__summary"><span>Your direction</span><strong>${escapeHTML(message.summary.title)}</strong><p>${escapeHTML(message.summary.copy)}</p><div>${safeActions(message.summary.actions).filter(action => action.href).map(action => `<a class="global-assistant__action${action.quiet ? ' global-assistant__action--quiet' : ''}" href="${escapeHTML(action.href)}">${escapeHTML(action.label)}</a>`).join('')}</div></div>` : '';
      return `<div class="global-assistant__message global-assistant__message--${message.role}"><p>${escapeHTML(message.text)}</p>${actions}${summary}</div>`;
    }).join('')}</div>`;
    requestAnimationFrame(() => { thread.scrollTop = thread.scrollHeight; });
  };

  const setBusy = value => {
    busy = value;
    root.classList.toggle('is-thinking', value);
    input.disabled = value;
    sendButton.hidden = value;
    stopButton.hidden = !value;
    status.textContent = value ? 'Considering your request' : 'Ready to help';
  };

  const add = message => { messages.push(ensureMessageId(message)); safeSession.write(); renderMessages(); };
  const thinkThen = callback => {
    setBusy(true);
    messages.push({type:'thinking'});
    renderMessages();
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      messages = messages.filter(message => message.type !== 'thinking');
      setBusy(false);
      callback();
    }, 520);
  };

  const recommendationQuestions = {
    space: {text:'Where will the arrangement live?', actions:[['Dining table','dining'],['Console or entryway','console'],['Shelf or bedside','shelf']]},
    mood: {text:'What should the room feel like?', actions:[['Airy and light','airy'],['Sculptural','sculptural'],['Soft colour','soft']]},
    scale: {text:'How much presence do you want?', actions:[['Quiet accent','compact'],['Balanced','balanced'],['Statement','statement']]}
  };
  const recommendationOrder = ['space','mood','scale'];
  const labels = {dining:'Dining table',console:'Console or entryway',shelf:'Shelf or bedside',airy:'Airy and light',sculptural:'Sculptural',soft:'Soft colour',compact:'Quiet accent',balanced:'Balanced',statement:'Statement'};

  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency',currency:window.theme?.currency || 'USD'}).format(Number(cents || 0) / 100);
  const rejectsExistingSets = value => /(?:do\s*not|don't|dont|not|no|without)\s+(?:want\s+|need\s+|show\s+|recommend\s+)?(?:an?\s+)?(?:existing\s+)?sets?\b|individual\s+(?:products?|pieces?)\s+only|不(?:想|要|需要|看|考虑|推荐).{0,6}(?:套装|成套)|别.{0,6}(?:套装|成套)|(?:只要?|使用|用|拿).{0,4}单品|单品.{0,8}(?:搭配|组合|组成)|自己.{0,8}(?:搭|配)/i.test(String(value || ''));
  const requestMessageFor = value => rejectsExistingSets(value)
    ? `${value}\n\nIntent clarification: create a custom editable arrangement from currently available individual products only. Do not return existing Set products.`
    : value;
  const productPriceMarkup = product => {
    const price = Number(product.price || 0);
    if (product.kind === 'set' && price <= 0) return '';
    const comparePrice = Number(product.compare_at_price || 0);
    const discount = comparePrice > price ? Math.round(((comparePrice - price) * 100) / comparePrice) : 0;
    return `<div class="global-assistant-product__price"><strong>${escapeHTML(money(price))}</strong>${discount ? `<s>${escapeHTML(money(comparePrice))}</s><span class="price-discount">${discount}% OFF</span>` : ''}</div>`;
  };
  const isFinishedSetProduct = product => product?.set_type === 'finished'
    || product?.recipe?.set_type === 'finished'
    || String(product?.handle || '').endsWith('-finished-set')
    || (Array.isArray(product?.tags) && product.tags.includes('finished-set'));
  const rankedRecords = (records, terms) => records.map((record,index) => {
    const haystack = `${record.search_text || ''} ${record.title || ''} ${record.handle || ''}`.toLowerCase();
    const score = terms.reduce((sum,term) => sum + (haystack.includes(term) ? 4 : 0), 0);
    return {record,score,index};
  }).sort((a,b) => b.score - a.score || a.index - b.index).map(entry => entry.record);

  const suggestedProducts = (terms, categories = [], limit = 3) => {
    const eligible = catalog.filter(record => record.available !== false && (!categories.length || categories.includes(record.category)) && record.handle !== context.product?.handle);
    return rankedRecords(eligible, terms).slice(0,limit);
  };

  const openStudioPlan = plan => {
    const lines = (plan?.lines || []).map(line => ({
      handle:String(line?.handle || line?.record?.handle || ''),
      quantity:Math.max(1, Math.min(12, Number(line?.quantity || 1)))
    })).filter(line => line.handle);
    if (!lines.length) return;
    const params = new URLSearchParams({
      view:'design-studio',guided:'0',source:'global-assistant',
      ai_plan:JSON.stringify({version:1,layoutIndex:Math.max(0,Math.min(2,Number(plan?.layoutIndex || 0))),lines})
    });
    window.location.assign(`/collections/all?${params.toString()}`);
  };

  const renderProductMessage = message => {
    ensureMessageId(message);
    const products = (message.products || []).slice(0,3);
    if (!products.length) return '';
    const cards = products.map(product => {
      const setHref = `/collections/all?view=design-studio&guided=0&set=${encodeURIComponent(product.studio_set_id || product.handle.replace(/-editable-set$/, ''))}&source=studio_assistant_set`;
      const actions = product.kind === 'set' && !isFinishedSetProduct(product)
        ? `<a href="${escapeHTML(product.url)}">View set</a><a href="${escapeHTML(setHref)}">Edit in DIY</a>`
        : `<a href="${escapeHTML(product.url)}">View</a>${product.variant_id && product.available !== false ? `<button type="button" data-assistant-product-add="${escapeHTML(product.variant_id)}">Add</button>` : ''}`;
      return `<article class="global-assistant-product">
      <a class="global-assistant-product__image" href="${escapeHTML(product.url)}" tabindex="-1">${product.image ? `<img src="${escapeHTML(product.image)}" alt="" loading="lazy">` : ''}</a>
      <div class="global-assistant-product__body"><h3>${escapeHTML(product.title)}</h3>${productPriceMarkup(product)}
      <div class="global-assistant-product__actions">${actions}</div></div>
    </article>`;
    }).join('');
    const bridge = message.bridge && !message.bridge.dismissed && !bridgesMuted ? `<div class="global-assistant__bridge"><p>${escapeHTML(message.bridge.copy)}</p><div><button type="button" data-assistant-bridge="${escapeHTML(message.bridge.id)}">${escapeHTML(message.bridge.label)}</button><button type="button" data-assistant-bridge-dismiss="${escapeHTML(message.bridge.id)}">Keep browsing</button></div></div>` : '';
    const importableProducts = products.filter(product => product?.kind !== 'set' && !isFinishedSetProduct(product) && product?.handle);
    const planMessage = messages.find(item => item.type === 'plans' && item.id === message.studioPlanMessageId);
    const planIndex = planMessage?.plans?.findIndex(plan => plan?.lines?.some(line => line?.record?.category !== 'vase' && line?.record?.handle)) ?? -1;
    const studioAction = planIndex >= 0
      ? `<button type="button" class="global-assistant__action global-assistant__products-edit" data-assistant-plan-edit="${planIndex}" data-assistant-message-id="${escapeHTML(planMessage.id)}">Edit full arrangement in DIY</button>`
      : importableProducts.some(product => product.category !== 'vase')
        ? `<button type="button" class="global-assistant__action global-assistant__products-edit" data-assistant-products-edit="${escapeHTML(message.id)}">Start with these in DIY</button>`
        : '';
    return `<div class="global-assistant__products-message"><div><p>${escapeHTML(message.title || 'Pieces to consider')}</p>${message.copy ? `<span>${escapeHTML(message.copy)}</span>` : ''}</div><div class="global-assistant__product-track">${cards}</div>${studioAction}${bridge}</div>`;
  };

  const createPreviewPlans = () => {
    const termsBySpace = {dining:['dining','table','low','bowl'],console:['console','entryway','tall','branch'],shelf:['shelf','bedside','bud','small']};
    const termsByMood = {airy:['white','ivory','cream','magnolia','glass','sage'],sculptural:['branch','greenery','emerald','stoneware','dark'],soft:['pink','blush','peony','rose','coral']};
    const focusTerms = String(recommendation.focus || '').toLowerCase().split(/[\s,]+/).filter(Boolean);
    const terms = [...focusTerms,...(termsBySpace[recommendation.space] || []),...(termsByMood[recommendation.mood] || [])];
    const group = category => rankedRecords(catalog.filter(record => record.available !== false && record.category === category && record.canvas_image), terms);
    let vases = group('vase');
    if (recommendation.vase) vases = [...vases].sort((a,b) => Number(b.handle === recommendation.vase) - Number(a.handle === recommendation.vase));
    const flowers = group('flower');
    const greenery = group('greenery');
    const lines = group('line');
    const quantities = {compact:[2,1,1],balanced:[3,2,1],statement:[4,3,2]}[recommendation.scale] || [3,2,1];
    const names = ['Quiet Magnolia','Coral Balance','Sculptural Burgundy'];
    const findPreferred = (records,handles,index) => handles.map(handle => records.find(record => record.handle === handle)).find(Boolean) || records[index % Math.max(1,records.length)];
    const recipes = [
      {vases:['emerald-glazed-low-bowl'],botanicals:[{records:flowers,handles:['ivory-magnolia-stem'],quantity:quantities[0]},{records:greenery,handles:['olive-veil-greenery'],quantity:quantities[1]}]},
      {vases:['onyx-ceramic-bud-vase','emerald-glazed-low-bowl'],botanicals:[{records:flowers,handles:['coral-garden-peony-stem'],quantity:quantities[0]},{records:greenery,handles:['fern-mist-greenery'],quantity:quantities[1]}]},
      {vases:['verdant-recycled-glass-vase','emerald-glazed-low-bowl'],botanicals:[{records:flowers,handles:['burgundy-garden-rose-stem'],quantity:quantities[0]},{records:lines,handles:['burnished-beech-branch'],quantity:quantities[1]},{records:greenery,handles:['seeded-sage-spray','olive-veil-greenery'],quantity:quantities[2]}]}
    ];
    return recipes.map((recipe,planIndex) => {
      const vase = findPreferred(vases,recipe.vases,planIndex);
      const chosen = recipe.botanicals.map((spec,index) => ({record:findPreferred(spec.records,spec.handles,planIndex + index),quantity:spec.quantity})).filter(line => line.record);
      const unique = chosen.filter((line,index,array) => array.findIndex(item => item.record === line.record) === index);
      const linesForPlan = [...(vase ? [{record:vase,quantity:1,isVase:true}] : []),...unique.map(line => ({...line,isVase:false}))];
      return {name:names[planIndex],lines:linesForPlan,total:linesForPlan.reduce((sum,line) => sum + Number(line.record.price || 0) * line.quantity,0)};
    }).filter(plan => plan.lines.length > 1);
  };

  const planRecordFor = line => ({...(line?.record || {}),...(catalogByHandle.get(String(line?.record?.handle || '')) || {})});
  const planCategoryLabel = category => ({vase:'Vase',flower:'Floral stem',greenery:'Greenery',line:'Branch'}[category] || 'Product');
  const renderPlanLineCard = (line,planIndex,lineIndex,messageId) => {
    const record = planRecordFor(line);
    const quantity = Math.max(1,Math.min(12,Number(line?.quantity || 1)));
    const unitPrice = Number(record.price || 0);
    const href = record.url || (record.handle ? `/products/${encodeURIComponent(record.handle)}` : '/collections/all');
    const available = record.available !== false && Boolean(record.variant_id);
    const addLabel = quantity > 1 ? `Add ${quantity}` : 'Add';
    return `<article class="global-assistant-plan-item${available ? '' : ' is-unavailable'}">
      <a class="global-assistant-plan-item__image" href="${escapeHTML(href)}" tabindex="-1">${record.image ? `<img src="${escapeHTML(record.image)}" alt="" loading="lazy">` : ''}</a>
      <div class="global-assistant-plan-item__body">
        <p class="global-assistant-plan-item__meta">${escapeHTML(planCategoryLabel(record.category))}${available ? '' : '<span>Unavailable</span>'}</p>
        <h4>${escapeHTML(record.title || 'Recommended product')}</h4>
        <div class="global-assistant-plan-item__quantity"><span>${quantity} × ${escapeHTML(money(unitPrice))}</span><strong>${escapeHTML(money(unitPrice * quantity))}</strong></div>
        <div class="global-assistant-plan-item__actions"><a href="${escapeHTML(href)}">View</a><button type="button" data-assistant-plan-item-add="${lineIndex}" data-assistant-plan-index="${planIndex}" data-assistant-message-id="${escapeHTML(messageId)}" data-assistant-idle-label="${escapeHTML(addLabel)}"${available ? '' : ' disabled'}>${available ? escapeHTML(addLabel) : 'Unavailable'}</button></div>
      </div>
    </article>`;
  };

  const renderPlanMessage = message => {
    ensureMessageId(message);
    const plans = (message.plans || []).slice(0,3);
    const cards = plans.map((plan,index) => {
      const lines = Array.isArray(plan.lines) ? plan.lines : [];
      const productCount = lines.length;
      const pieceCount = lines.reduce((sum,line) => sum + Number(line.quantity || 1),0);
      const total = lines.reduce((sum,line) => sum + Number(planRecordFor(line).price || 0) * Math.max(1,Number(line.quantity || 1)),0);
      const allAvailable = lines.length > 0 && lines.every(line => planRecordFor(line).available !== false && planRecordFor(line).variant_id);
      const requestedLayout = Number(plan?.layoutIndex);
      plan.layoutIndex = Number.isInteger(requestedLayout) ? Math.max(0,Math.min(2,requestedLayout)) : index % 3;
      return `<section class="global-assistant-plan">
        <div class="global-assistant-plan__header"><div><h3>${escapeHTML(plan.name || 'Selected individual pieces')}</h3><p>${productCount} products, ${pieceCount} pieces</p></div><strong>${escapeHTML(money(total || plan.total))}</strong></div>
        <div class="global-assistant-plan__items">${lines.map((line,lineIndex) => renderPlanLineCard(line,index,lineIndex,message.id)).join('')}</div>
        <div class="global-assistant-plan__actions"><button type="button" class="global-assistant__action" data-assistant-set-add="${index}" data-assistant-message-id="${escapeHTML(message.id)}" data-assistant-idle-label="Add all"${allAvailable ? '' : ' disabled'}>${allAvailable ? 'Add all' : 'Some items unavailable'}</button><button type="button" class="global-assistant__action global-assistant__action--quiet" data-assistant-plan-edit="${index}" data-assistant-message-id="${escapeHTML(message.id)}">Edit in DIY</button></div>
      </section>`;
    }).join('');
    const firstProductCount = (plans[0]?.lines || []).length;
    const heading = plans.length === 1 ? `I picked ${firstProductCount} individual products.` : `I prepared ${plans.length} product directions.`;
    return `<div class="global-assistant__plans-message"><div class="global-assistant__plans-heading"><p>${heading}</p><span>Review each product, then add the selection or open it in DIY.</span></div><div class="global-assistant__plan-track">${cards}</div><button type="button" class="global-assistant__choice" data-assistant-revise>Adjust direction</button></div>`;
  };

  const askRecommendationQuestion = () => {
    const key = recommendationOrder.find(item => !recommendation[item]);
    if (!key) {
      if (root.dataset.apiEnabled === 'true' || window.FLORALSCULPT_AI_ENABLED === true) {
        const space = labels[recommendation.space] || recommendation.space || 'not specified';
        const mood = labels[recommendation.mood] || recommendation.mood || 'not specified';
        const scale = labels[recommendation.scale] || recommendation.scale || 'not specified';
        requestAI(`Build custom editable arrangements from currently available individual products only. Do not recommend existing Sets. Placement: ${space}. Mood: ${mood}. Scale: ${scale}.`);
      } else {
        const plans = createPreviewPlans();
        if (plans.length) add({type:'plans',plans});
        else add({role:'assistant',text:'I have the direction, but there are not enough available individual pieces to build a reliable arrangement preview right now.',actions:[{label:'Build freely',href:'/collections/all?view=design-studio&guided=0'}]});
      }
      return;
    }
    const question = recommendationQuestions[key];
    add({role:'assistant',text:question.text,actions:question.actions.map(([label,value]) => ({label,value:`recommend:${key}:${value}`}))});
  };

  const startRecommendation = (addRequest = true, seed = {}, options = {}) => {
    recommendation = {...seed};
    if (addRequest) add({role:'user',text:'Help me find an arrangement.'});
    thinkThen(() => {
      const productTitle = String(options.product?.title || '').trim();
      const productIntro = productTitle
        ? `Let’s build around ${productTitle}. I’ll use it as the starting reference and ask only for details that change the supporting pieces.`
        : '';
      add({
        role:'assistant',
        text:productIntro || (Object.keys(seed).length ? 'I’ll carry that into the arrangement and ask only for the details that are still missing.' : 'Of course. I’ll ask only what changes the recommendation, then I’ll prepare complete options you can edit.'),
        productSeed:String(options.product?.handle || '')
      });
      askRecommendationQuestion();
    });
  };

  const replyWithProducts = (text, terms, categories, title, bridge = null) => {
    thinkThen(() => {
      add({role:'assistant',text});
      const products = suggestedProducts(terms,categories,3);
      if (products.length) add({type:'products',title,copy:'Selected from currently available pieces.',products,bridge:bridge && !bridgesMuted ? {id:`bridge-${Date.now()}`, ...bridge} : null});
    });
  };

  const localReply = value => {
    const normalized = value.toLowerCase();
    const chinese = /[\u3400-\u9fff]/.test(value);
    if (/who are you|what are you|what can you do|你是谁|你能做什么|介绍.{0,4}自己/.test(normalized)) {
      thinkThen(() => add({
        role:'assistant',
        text:chinese ? '我是 FLORALSCULPT 的 AI 花艺师。我可以直接回答花材搭配、花瓶比例、空间布置、养护和商品问题，也可以根据店铺现有商品生成多个完整方案，并把你选中的方案送进 Design Studio 继续编辑。只有当你明确想要方案时，我才会询问空间、风格和尺寸。' : 'I’m FLORALSCULPT’s AI florist. I can directly answer questions about flower pairings, vase proportion, styling, care and products. I can also shape complete options from current store pieces and send the one you choose into the Design Studio. I only ask about placement, mood and scale when you want a recommendation.',
        actions:[{label:chinese ? '问一个搭配问题' : 'Ask a pairing question',value:`prompt:${chinese ? '玫瑰适合搭配什么花？' : 'What pairs well with roses?'}`},{label:chinese ? '帮我搭一套' : 'Build a set',value:'start-recommendation'}]
      }));
      return;
    }
    if (/rose|玫瑰/.test(normalized)) {
      replyWithProducts(
        chinese ? '玫瑰可以搭配三种方向。想要轻盈自然，可以加入绣球、洋桔梗和少量绿植；想要柔和浪漫，可以搭配牡丹或浅色小花；想要更有线条感，可以加入枝条和结构型绿植。花材不宜全部同样饱满，保留一点高低和空隙会更自然。' : 'Roses work in three useful directions. Pair them with hydrangea, lisianthus and light greenery for an airy arrangement; add peony or softer blooms for a romantic result; or use branches and structured greenery for a more sculptural line. Keep some height variation and breathing room so the arrangement does not feel uniformly full.',
        ['rose','hydrangea','lisianthus','peony','eucalyptus','greenery'],['flower','greenery','line'],chinese ? '适合与玫瑰搭配' : 'Pieces that pair with roses',
        {copy:chinese ? '想看看这些花组合起来的整体效果吗？' : 'Want to see these pieces composed together?',label:chinese ? '用玫瑰搭一套' : 'Create with roses',focus:'rose'}
      );
      return;
    }
    if (/hydrangea|绣球/.test(normalized)) {
      replyWithProducts(
        chinese ? '绣球体量较大，通常适合作为视觉主体或柔和的体积层。可以搭配玫瑰、牡丹或玉兰，再用一两支绿植拉开轮廓。窄口花瓶不需要太多支，宽口花器则要增加枝叶结构来支撑。' : 'Hydrangea has strong visual volume, so it works best as a focal bloom or a soft mass. Pair it with roses, peony or magnolia, then add one or two greenery stems to open the silhouette. Use fewer stems in a narrow vase and more structure in a wide vessel.',
        ['hydrangea','rose','peony','magnolia','greenery'],['flower','greenery'],chinese ? '可搭配的花材' : 'Complementary stems',
        {copy:chinese ? '要把这些花材组合成完整方案吗？' : 'Would you like to see these as a complete arrangement?',label:chinese ? '用绣球搭一套' : 'Create with hydrangea',focus:'hydrangea'}
      );
      return;
    }
    if (/magnolia|玉兰/.test(normalized)) {
      replyWithProducts(
        chinese ? '玉兰本身线条感很强，适合少量成组使用。可以搭配橄榄枝、尤加利或较轻的白色花材，但不要加入太多同样大型的花头。高挑花瓶适合保留枝条走势，低花器则更适合剪短后做横向结构。' : 'Magnolia already has a strong line, so it is most effective in a restrained group. Pair it with olive, eucalyptus or lighter white blooms rather than several competing focal flowers. Keep the branch length in a tall vase, or cut it lower for a wider composition.',
        ['magnolia','olive','eucalyptus','white','ivory'],['flower','greenery','line'],chinese ? '玉兰搭配建议' : 'Pieces for magnolia',
        {copy:chinese ? '要把玉兰和这些花材组合起来看看吗？' : 'Want to see magnolia composed with these pieces?',label:chinese ? '用玉兰搭一套' : 'Create with magnolia',focus:'magnolia'}
      );
      return;
    }
    if (/price|cost|多少钱|价格/.test(normalized) && context.product) {
      if (context.product.editable_set) {
        const studioHref = `/collections/all?view=design-studio&guided=0&set=${encodeURIComponent(context.product.studio_set_id || '')}&source=studio_assistant_product`;
        thinkThen(() => add({role:'assistant',text:chinese ? '这是一个由 Shop All 单品组成的可编辑方案，不是单独售卖的整套商品。总价会根据你在 Design Studio 中保留的花瓶、花材和数量实时计算。' : 'This is an editable composition built from individual Shop All products, not one bundled item. Its total updates from the vase, stems and quantities you keep in Design Studio.',actions:[{label:chinese ? '打开 DIY' : 'Open Design Studio',href:studioHref}]}));
        return;
      }
      thinkThen(() => add({role:'assistant',text:chinese ? `${context.product.title} 当前价格为 ${money(context.product.price)}。价格和库存以商品页面当前选择的规格为准。` : `${context.product.title} is currently ${money(context.product.price)}. Price and availability can change with the selected variant, so the product page remains the final reference.`,actions:[{label:'View product',href:context.product.url}]}));
      return;
    }
    if (/available|stock|库存|有货/.test(normalized) && context.product) {
      if (context.product.editable_set) {
        const studioHref = `/collections/all?view=design-studio&guided=0&set=${encodeURIComponent(context.product.studio_set_id || '')}&source=studio_assistant_product`;
        thinkThen(() => add({role:'assistant',text:chinese ? '这套方案没有独立库存。Design Studio 会检查其中每个 Shop All 单品的当前库存，并标出需要替换的商品。' : 'This composition has no separate inventory. Design Studio checks each Shop All product and marks any piece that needs replacing.',actions:[{label:chinese ? '检查单品库存' : 'Check component availability',href:studioHref}]}));
        return;
      }
      thinkThen(() => add({role:'assistant',text:context.product.available ? (chinese ? '这个商品当前可以购买。具体规格的库存请以商品页选项状态为准。' : 'This product is currently available. Check the selected option on the product page for variant-level availability.') : (chinese ? '这个商品目前显示为暂时无货。' : 'This product is currently shown as unavailable.'),actions:[{label:'View product',href:context.product.url}]}));
      return;
    }
    if (/build.*arrangement|complete arrangement|full set|arrangement for me|套装|整套|搭一套|做一套|完整花艺|帮我搭|花束/.test(normalized)) { startRecommendation(false); return; }
    if ((/pair|go with|搭配|配什么/.test(normalized)) && context.product) {
      replyWithProducts(
        chinese ? `可以。我会先围绕 ${context.product.title} 的体量和视觉方向选择互补花材，避免多个主花互相争抢注意力。你也可以告诉我它要放在餐桌、玄关还是床头，我可以进一步控制高度和数量。` : `Yes. I would build around the scale and visual character of ${context.product.title}, using supporting stems rather than several competing focal flowers. Tell me whether it is for a dining table, console or bedside if you want a more precise proportion.`,
        String(context.product.title || '').toLowerCase().split(/\s+/),['flower','greenery','line'],chinese ? '可以考虑这些单品' : 'Pieces to consider',
        {copy:chinese ? '要以当前商品为起点生成完整搭配吗？' : 'Want to build a complete arrangement around this piece?',label:chinese ? '以这款商品搭配' : 'Build with this piece',focus:context.product.handle,vase:/vase/i.test(context.product.type || '') ? context.product.handle : null}
      );
      return;
    }
    if (/vase|opening|ceramic|glass|花瓶|花器|陶瓷|玻璃/.test(normalized)) {
      replyWithProducts(chinese ? '选择花瓶时，先看摆放位置和想要的轮廓。餐桌更适合低矮或宽口花器，避免遮挡视线；玄关可以使用更高、更有结构感的花瓶；床头和书架适合小型窄口花瓶，少量花材也容易保持形状。' : 'Start with placement and silhouette. Dining tables usually suit lower or wider vessels that keep sightlines open; consoles can take more height and structure; shelves and bedside tables work well with compact narrow-opening vases that hold a small number of stems.', ['vase','ceramic','glass','bowl'],['vase'],chinese ? '花瓶推荐' : 'Vases to consider',{copy:chinese ? '选好花瓶后，也可以直接生成一套完整花艺。' : 'Once you choose a vase, I can build a complete arrangement around it.',label:chinese ? '生成完整搭配' : 'Build an arrangement'});
      return;
    }
    if (/delivery|shipping|return|care|clean|到货|配送|退货|清洁|保养/.test(normalized)) {
      thinkThen(() => add({role:'assistant',text:chinese ? '仿真花日常可以用柔软干布或低档冷风除尘，避免长时间阳光直射和浸水。配送、退货或订单相关问题需要以商店政策和具体订单状态为准。' : 'For faux florals, use a soft dry cloth or cool low-power air to remove dust, and avoid prolonged direct sunlight or soaking. Delivery, returns and order-specific questions should always follow the current store policy and order status.',actions:[{label:'View FAQs',href:'/collections/all?view=faq'}]}));
      return;
    }
    if (/style|room|table|console|shelf|空间|房间|餐桌|玄关|书架/.test(normalized)) {
      const space = /console|entryway|玄关/.test(normalized) ? 'console' : /shelf|bedside|书架|床头/.test(normalized) ? 'shelf' : /table|餐桌/.test(normalized) ? 'dining' : null;
      const spaceAdvice = space === 'console' ? (chinese ? '玄关或边柜适合有明确正面轮廓的花艺，高度可以更大胆，但底部占地要克制，避免挤压钥匙、灯具和日常物品。' : 'A console or entryway suits a clear front-facing silhouette. You can use more height, but keep the footprint controlled so the arrangement does not compete with lamps and everyday objects.') : space === 'shelf' ? (chinese ? '书架和床头更适合小体量花艺。选择窄口花瓶和少量花材，保留负空间，近距离观看会更自然。' : 'Shelves and bedside tables suit compact arrangements. Use a narrow-opening vase, fewer stems and visible negative space so the result feels natural at close range.') : space === 'dining' ? (chinese ? '餐桌花艺应优先保证交流视线。建议采用低矮、四周都能观看的轮廓，并控制枝条向外延伸的范围。' : 'For a dining table, keep conversation sightlines open. A lower arrangement with an all-round silhouette works best, with the outer spread kept within the usable table area.') : (chinese ? '告诉我具体摆放位置、台面大致尺寸，以及你希望空间偏轻盈、柔和还是更有雕塑感，我可以给出更准确的比例建议。' : 'Tell me the surface, its approximate size, and whether you want the room to feel airy, soft or more sculptural. I can then give you a more precise proportion.');
      const actions = space
        ? [{label:chinese ? `为${space === 'console' ? '玄关' : space === 'shelf' ? '书架或床头' : '餐桌'}搭一套` : `Design for my ${space === 'console' ? 'console' : space === 'shelf' ? 'shelf' : 'dining table'}`,value:`bridge-space:${space}`},{label:chinese ? '进入 DIY' : 'Open DIY',href:'/collections/all?view=design-studio&guided=0',quiet:true}]
        : [{label:'Dining table',value:'recommend:space:dining'},{label:'Console or entryway',value:'recommend:space:console'},{label:'Shelf or bedside',value:'recommend:space:shelf'}];
      thinkThen(() => add({role:'assistant',text:spaceAdvice,actions}));
      return;
    }
    if (/diy|studio|design|自己做|自己搭/.test(normalized)) {
      thinkThen(() => add({role:'assistant',text:'You can start with a blank canvas, or I can first shape a complete recommendation and send it to the Studio for editing.',actions:[{label:'Recommend a starting point',value:'start-recommendation'},{label:'Open blank Studio',href:'/collections/all?view=design-studio&guided=0',quiet:true}]}));
      return;
    }
    thinkThen(() => add({role:'assistant',text:chinese ? '我可以回答花材搭配、花瓶选择、空间布置、养护和商品问题，也可以根据你的空间生成可编辑的整套方案。你可以直接告诉我花材名称、摆放位置或想要的效果。' : 'I can answer questions about flower pairings, vases, styling, care and products, or build an editable set for your space. Tell me the flower, placement or result you have in mind.',actions:[{label:chinese ? '问搭配问题' : 'Ask about pairing',value:'prompt:What pairs well with roses?'},{label:chinese ? '帮我搭一套' : 'Build a set',value:'start-recommendation'}]}));
  };

  const requestAI = async value => {
    if (root.dataset.apiEnabled !== 'true' && window.FLORALSCULPT_AI_ENABLED !== true) { localReply(value); return; }
    const requestMessage = requestMessageFor(value);
    const excludeSets = rejectsExistingSets(requestMessage);
    requestController = new AbortController();
    setBusy(true);
    messages.push({type:'thinking'});
    renderMessages();
    try {
      const response = await fetch(root.dataset.apiEndpoint, {
        method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},signal:requestController.signal,
        body:JSON.stringify({message:requestMessage,conversation:messages.filter(item => item.role).slice(-12),context,recommendation})
      });
      if (!response.ok) throw new Error('Assistant request failed');
      const result = await response.json();
      messages = messages.filter(message => message.type !== 'thinking');
      const responseProducts = (Array.isArray(result.products) ? result.products : []).filter(product => !excludeSets || product?.kind !== 'set');
      const productMessage = responseProducts.length ? ensureMessageId({type:'products',title:excludeSets ? 'Pieces to consider' : result.products_title || 'Pieces to consider',copy:excludeSets ? 'Selected from currently available individual pieces.' : result.products_copy || '',products:responseProducts.slice(0,3)}) : null;
      const planMessage = Array.isArray(result.plans) && result.plans.length ? ensureMessageId({type:'plans',plans:result.plans.slice(0,3)}) : null;
      if (productMessage) productMessage.studioPlanMessageId = planMessage?.id;
      add({role:'assistant',text:result.message || 'How else can I help?',actions:Array.isArray(result.actions) ? result.actions : [],studioPlanMessageId:planMessage?.id,studioProductMessageId:productMessage?.id});
      if (productMessage) add(productMessage);
      if (planMessage) add(planMessage);
    } catch (error) {
      messages = messages.filter(message => message.type !== 'thinking');
      if (error.name !== 'AbortError') add({role:'assistant',text:/[\u3400-\u9fff]/.test(value)
        ? '这次暂时没有完成回复，请稍后重试。配送、退货或订单问题也可以查看 FAQs，或联系 service@floralsculpt.com；客服通常在 1–3 个工作日内回复。此聊天没有代你提交任何申请。'
        : 'I could not complete that response. Please try again, check our FAQs or contact service@floralsculpt.com. The team normally replies within 1–3 business days. No request has been submitted on your behalf.',
        actions:[{label:'FAQs',href:'/collections/all?view=faq'},{label:'Contact us',href:'/pages/contact'}]});
    } finally { requestController = null; setBusy(false); }
  };

  const configureProductEntry = product => {
    if (!product?.handle) return null;
    const normalizedProduct = {
      handle:String(product.handle),
      title:String(product.title || 'this piece'),
      type:String(product.type || ''),
      category:String(product.category || ''),
      available:Boolean(product.available),
      editable_set:Boolean(product.editable_set),
      studio_set_id:String(product.studio_set_id || ''),
      url:String(product.url || `/products/${encodeURIComponent(product.handle)}`)
    };
    context = {...context,page_type:'design_studio',product:normalizedProduct};
    const [chooseButton,complementButton,exploreButton] = quickPromptButtons;
    const isVase = normalizedProduct.category === 'vase';
    if (chooseButton) {
      chooseButton.textContent = isVase ? 'Choose flowers' : 'Choose a vase';
      chooseButton.dataset.assistantPrompt = isVase
        ? `Help me choose available flowers for ${normalizedProduct.title}. Keep this vase as the starting piece.`
        : `Help me choose an available vase for ${normalizedProduct.title}. Keep this product as the starting piece.`;
    }
    if (complementButton) {
      complementButton.textContent = 'Add supporting pieces';
      complementButton.dataset.assistantPrompt = `Recommend complementary individual pieces for ${normalizedProduct.title}. Do not return Sets.`;
    }
    if (exploreButton) {
      exploreButton.textContent = 'Explore freely';
      exploreButton.removeAttribute('data-assistant-prompt');
      exploreButton.setAttribute('data-assistant-explore', '');
    }
    return normalizedProduct;
  };

  const open = (intent, seed = {}, entry = {}) => {
    const productEntry = configureProductEntry(entry.product);
    root.classList.add('is-open');
    root.setAttribute('aria-hidden','false');
    openers.forEach(opener => opener.setAttribute('aria-expanded','true'));
    document.body.classList.add('global-assistant-open');
    if (!messages.length) renderWelcome(); else renderMessages();
    window.setTimeout(() => panel.focus(), 40);
    const recommendationStarted = Object.keys(recommendation).length > 0
      || messages.some(message => message.type === 'plans')
      || messages.some(message => message.role === 'assistant' && Object.values(recommendationQuestions).some(question => question.text === message.text));
    const productRecommendationStarted = productEntry && messages.some(message => message.productSeed === productEntry.handle);
    if (intent === 'recommend' && !busy && (productEntry ? !productRecommendationStarted : !recommendationStarted)) {
      startRecommendation(!Object.keys(seed || {}).length, seed || {}, {product:productEntry});
    }
  };
  const close = () => {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden','true');
    openers.forEach(opener => opener.setAttribute('aria-expanded','false'));
    document.body.classList.remove('global-assistant-open');
    (lastOpener?.isConnected ? lastOpener : launcher).focus({preventScroll:true});
  };
  openers.forEach(opener => opener.addEventListener('click', () => {
    lastOpener = opener;
    open();
  }));

  const addCartItems = async (items, sourceButton) => {
    if (!items.length || busy) return;
    setBusy(true);
    if (sourceButton) { sourceButton.disabled = true; sourceButton.textContent = 'Adding'; }
    try {
      const route = window.theme?.routes?.cartAdd || '/cart/add';
      const response = await fetch(`${route}.js`, {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items})});
      if (!response.ok) throw new Error('Unable to add these items');
      close();
      window.setTimeout(() => document.querySelector('[data-open-cart]')?.click(),120);
    } catch (_) {
      add({role:'assistant',text:'I could not add that selection. One of the pieces may have changed availability. Please review the set or try again.'});
    } finally {
      setBusy(false);
      if (sourceButton?.isConnected) { sourceButton.disabled = false; sourceButton.textContent = sourceButton.dataset.assistantIdleLabel || (sourceButton.hasAttribute('data-assistant-set-add') ? 'Add all' : 'Add'); }
    }
  };

  document.addEventListener('click', event => {
    const assistantOpener = event.target.closest('[data-global-assistant-open]');
    if (assistantOpener) { lastOpener = assistantOpener; open(); return; }
    if (event.target.closest('[data-global-assistant-close]')) { close(); return; }
    if (event.target.closest('[data-global-assistant-reset]')) {
      window.clearTimeout(timer); requestController?.abort(); requestController = null; busy = false; messages = []; recommendation = {}; bridgesMuted = false; safeSession.clear(); setBusy(false); renderWelcome(); input.focus(); return;
    }
    if (event.target.closest('[data-assistant-explore]')) { close(); return; }
    const starter = event.target.closest('[data-assistant-prompt]')?.dataset.assistantPrompt;
    if (starter && !busy) { add({role:'user',text:starter}); requestAI(starter); return; }
    const productAdd = event.target.closest('[data-assistant-product-add]');
    if (productAdd) { addCartItems([{id:productAdd.dataset.assistantProductAdd,quantity:1,properties:{_floree_source:'studio_assistant'}}],productAdd); return; }
    const planItemAdd = event.target.closest('[data-assistant-plan-item-add]');
    if (planItemAdd) {
      const planMessage = messages.find(message => message.type === 'plans' && message.id === planItemAdd.dataset.assistantMessageId);
      const plan = planMessage?.plans?.[Number(planItemAdd.dataset.assistantPlanIndex)];
      const line = plan?.lines?.[Number(planItemAdd.dataset.assistantPlanItemAdd)];
      const record = planRecordFor(line);
      if (!record.variant_id || record.available === false) return;
      addCartItems([{id:String(record.variant_id),quantity:Math.max(1,Math.min(12,Number(line?.quantity || 1))),properties:{_floree_source:'studio_assistant',_floree_plan:plan.name}}],planItemAdd);
      return;
    }
    const productsEdit = event.target.closest('[data-assistant-products-edit]');
    if (productsEdit) {
      const productMessage = messages.find(message => message.type === 'products' && message.id === productsEdit.dataset.assistantProductsEdit);
      const products = (productMessage?.products || []).filter(product => product?.kind !== 'set' && !isFinishedSetProduct(product) && product?.handle);
      const vase = products.find(product => product.category === 'vase');
      const botanicals = products.filter(product => product !== vase && product.category !== 'vase').slice(0,3);
      const lines = [...(vase ? [{record:vase,quantity:1,isVase:true}] : []),...botanicals.map(record => ({record,quantity:1,isVase:false}))];
      openStudioPlan({layoutIndex:0,lines});
      return;
    }
    const planEdit = event.target.closest('[data-assistant-plan-edit]');
    if (planEdit) {
      const planMessage = messages.find(message => message.type === 'plans' && message.id === planEdit.dataset.assistantMessageId);
      openStudioPlan(planMessage?.plans?.[Number(planEdit.dataset.assistantPlanEdit)]);
      return;
    }
    const setAdd = event.target.closest('[data-assistant-set-add]');
    if (setAdd) {
      const planMessage = messages.find(message => message.type === 'plans' && message.id === setAdd.dataset.assistantMessageId);
      const plan = planMessage?.plans?.[Number(setAdd.dataset.assistantSetAdd)];
      const items = (plan?.lines || []).map(line => ({line,record:planRecordFor(line)})).filter(item => item.record.variant_id && item.record.available !== false).map(item => ({id:String(item.record.variant_id),quantity:Number(item.line.quantity || 1),properties:{_floree_source:'studio_assistant',_floree_plan:plan.name}}));
      addCartItems(items,setAdd); return;
    }
    const bridgeButton = event.target.closest('[data-assistant-bridge]');
    if (bridgeButton && !busy) {
      const productMessage = [...messages].reverse().find(message => message.type === 'products' && message.bridge?.id === bridgeButton.dataset.assistantBridge);
      if (!productMessage?.bridge) return;
      const seed = {};
      if (productMessage.bridge.focus) seed.focus = productMessage.bridge.focus;
      if (productMessage.bridge.vase) seed.vase = productMessage.bridge.vase;
      add({role:'user',text:productMessage.bridge.label});
      startRecommendation(false,seed);
      return;
    }
    const dismissBridge = event.target.closest('[data-assistant-bridge-dismiss]');
    if (dismissBridge) {
      bridgesMuted = true;
      messages.forEach(message => { if (message.bridge) message.bridge.dismissed = true; });
      safeSession.write(); renderMessages(); return;
    }
    const intent = event.target.closest('[data-assistant-intent]')?.dataset.assistantIntent;
    if (intent === 'recommend') { startRecommendation(); return; }
    if (intent === 'vase') { add({role:'user',text:'Help me choose a vase.'}); localReply('vase help'); return; }
    if (intent === 'style') { add({role:'user',text:'Help me style a room.'}); thinkThen(() => add({role:'assistant',text:'Tell me what surface you are styling, its approximate size, and the feeling you want. A photo can be supported when the live AI service is connected.',actions:[{label:'Dining table',value:'recommend:space:dining'},{label:'Console or entryway',value:'recommend:space:console'},{label:'Shelf or bedside',value:'recommend:space:shelf'}]})); return; }
    if (intent === 'care') { add({role:'user',text:'I have a product question.'}); localReply('care delivery product question'); return; }
    if (event.target.closest('[data-assistant-revise]')) {
      recommendation = {};
      add({role:'assistant',text:'Let’s adjust it. Where will the arrangement live?'});
      askRecommendationQuestion();
      return;
    }
    const choice = event.target.closest('[data-assistant-choice]')?.dataset.assistantChoice;
    if (!choice || busy) return;
    if (choice === 'start-recommendation') { startRecommendation(); return; }
    if (choice.startsWith('bridge-space:')) {
      const space = choice.split(':')[1];
      add({role:'user',text:labels[space] || space});
      startRecommendation(false,{space});
      return;
    }
    if (choice.startsWith('prompt:')) { const prompt = choice.slice(7); add({role:'user',text:prompt}); requestAI(prompt); return; }
    if (choice.startsWith('intent:')) { const next = choice.split(':')[1]; if (next === 'care') localReply('care product question'); if (next === 'style') localReply('style my room'); return; }
    if (choice.startsWith('recommend:')) {
      const [,key,value] = choice.split(':');
      recommendation[key] = value;
      add({role:'user',text:labels[value] || value});
      thinkThen(askRecommendationQuestion);
    }
  });

  form.addEventListener('submit', event => {
    event.preventDefault();
    const value = input.value.trim();
    if (!value || busy) return;
    input.value = '';
    input.style.height = '';
    add({role:'user',text:value});
    requestAI(value);
  });
  input.addEventListener('input', () => { input.style.height = 'auto'; input.style.height = `${Math.min(input.scrollHeight,112)}px`; });
  input.addEventListener('keydown', event => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); form.requestSubmit(); } });
  stopButton.addEventListener('click', () => {
    window.clearTimeout(timer);
    requestController?.abort();
    requestController = null;
    messages = messages.filter(message => message.type !== 'thinking');
    setBusy(false);
    add({role:'assistant',text:'Stopped. Your conversation is still here, so you can continue in another direction whenever you are ready.'});
  });
  document.addEventListener('keydown', event => { if (event.key === 'Escape' && root.classList.contains('is-open')) close(); });
  document.addEventListener('floralsculpt:assistant-open', event => open(event.detail?.intent, event.detail?.recommendation, event.detail));

  root.dataset.globalAssistantReady = 'true';
  document.dispatchEvent(new CustomEvent('floralsculpt:assistant-ready'));
  renderMessages();
})();
